// "Preview as player" in the open condition builder (spec/dashboard.md section 7.1): the shared environment selector, a
// player (a recent one, or an id looked up by its shape, spec section 11.1), a game time and the session facts, the
// player's saved profiles read through the player page's hooks, and evaluate.ts over the builder's root — the root verdict
// here, a badge per leaf row in the tree. The server has no condition evaluator (spec/liveops-engine.md section 5.1 runs in
// the SDK), so this is the dashboard's reading of the engine rules; the game decides.
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { ops } from '../../api/operations';
import type { Environment, Json, Page, Player, SaveCollectionDetail } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { guessIdKinds } from '../../players/idKind';
import { obj, shortPlayerId } from '../../players/model';
import { playerPrefix, useCollection, usePlayer } from '../../players/usePlayer';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Badge, type Tone } from '../../ui/Chip';
import { Field } from '../../ui/Field';
import { Select, TextInput } from '../../ui/inputs';
import { formatInstant, formatInstantWithZone, useTimeZone } from '../../ui/time';
import { DateTimeInput } from '../../ui/values';
import { useQuery, type QueryState } from '../../workspace/cache';
import { useContent } from '../ContentContext';
import { EnvSelector, useLiveOpsEnv } from '../liveops/env';
import { evaluateCondition, readPath, type EvalResult, type EvalState, type LeafVerdict, type SessionFacts } from './evaluate';
import { CONDITION_NS } from './model';
import { collectTree } from './ops';

const SYSTEM_COLLECTION = 'UnnyProfile';
const SYSTEM_PROFILE = 'SmartObjects.UnnyProfile';
const PROFILE_LEAVES = new Set(['ProfileFieldNumber', 'ProfileFieldLong', 'ProfileFieldString', 'ProfileFieldBool', 'ProfileFieldInRange', 'ProfileFieldList'].map((k) => CONDITION_NS + k));

/** The game-time offset of an environment (spec/liveops-engine.md section 3): production and device-local time read UTC here. */
export function gameOffsetOf(env: Pick<Environment, 'kind' | 'gameTimeOffsetSeconds' | 'useLocalTime'> | null | undefined): number {
  if (!env || env.kind === 'prod' || env.useLocalTime) return 0;
  return Number(env.gameTimeOffsetSeconds ?? 0) || 0;
}

const short = (name: string) => name.split('.').pop() ?? name;
const str = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : typeof v === 'number' ? String(v) : undefined);

export interface ConditionPreview {
  env: Environment | null;
  playerId: string;
  setPlayerId: (id: string) => void;
  /** the chosen instant in server unix seconds; null = now */
  at: number | null;
  setAt: (seconds: number | null) => void;
  /** the instant evaluated, server seconds, and the game time it reads */
  serverNow: number;
  gameNow: number;
  /** the session facts in effect: the typed ones over those read from the player record and its GeneralInfo */
  session: SessionFacts;
  setSessionFact: (key: keyof SessionFacts, value: string) => void;
  player: QueryState<Player>;
  loading: boolean;
  error: unknown;
  /** null until a player is chosen and its profiles are read */
  result: EvalResult | null;
}

/**
 * The preview's state and verdicts for a builder root (spec/dashboard.md section 7.1), reusable by other pages: the player
 * and the collections its tree reads (the system profile and the custom roots its leaves name), evaluated at the chosen time.
 */
export function useConditionPreview(rootId: string | null): ConditionPreview {
  const catalog = useContent();
  const { client } = useAuth();
  const { org, game } = useGame();
  const { env } = useLiveOpsEnv();
  const envSlug = env?.slug ?? '';
  const [playerId, setPlayerIdState] = useState('');
  const [at, setAt] = useState<number | null>(null);
  const [typed, setTyped] = useState<SessionFacts>({});
  const setPlayerId = (id: string) => {
    setPlayerIdState(id);
    setTyped({});
  };
  const player = usePlayer(envSlug, envSlug ? playerId : '');
  const has = (c: string) => (player.data?.collections ?? []).some((x) => x.collection === c);
  const system = useCollection(envSlug, playerId, player.data && has(SYSTEM_COLLECTION) ? SYSTEM_COLLECTION : null);

  // the custom roots the tree's profile leaves name, by collection (section 17.2: the collection is the root's short name)
  const tree = collectTree(catalog, rootId);
  const customNames = Array.from(
    new Set(
      tree.ids
        .map((id) => catalog.resolve(id))
        .filter((n) => n && PROFILE_LEAVES.has(n.template))
        .map((n) => str(obj(obj(n!.data)?.fullPath)?.profile))
        .filter((p): p is string => !!p && p !== SYSTEM_PROFILE && p !== SYSTEM_COLLECTION && has(short(p)))
        .map(short),
    ),
  ).sort();
  const customKey = player.data && customNames.length ? `${playerPrefix(game.id, envSlug, playerId)}:preview:${customNames.join(',')}` : null;
  const custom = useQuery(customKey, async () => {
    const out: Record<string, Json> = {};
    for (const c of customNames) {
      const detail = (await client.data(ops.getPlayerCollection, { path: { org, game: game.slug, env: envSlug, player: playerId, collection: c } })) as SaveCollectionDetail;
      out[c] = valueOf(detail);
    }
    return out;
  });

  const systemValue = system.data ? valueOf(system.data) : undefined;
  const offset = gameOffsetOf(env);
  const [clock] = useState(() => Math.floor(Date.now() / 1000));
  const serverNow = at ?? clock;
  const gameNow = serverNow + offset;

  const general = obj(readPath(systemValue, 'GeneralInfo').value);
  const read: SessionFacts = {
    appVersion: str(general?.appVersion),
    engineVersion: str(general?.engineVersion),
    platform: str(general?.platform),
    balancyPlatform: str(player.data?.lastPlatform),
    language: str(general?.systemLanguage),
    country: str(player.data?.country) ?? str(general?.country),
  };
  const session: SessionFacts = {};
  for (const k of Object.keys(read) as (keyof SessionFacts)[]) {
    const v = k in typed ? typed[k] : read[k];
    if (v !== undefined && v !== '') (session as Record<string, unknown>)[k] = v;
  }
  const setSessionFact = (key: keyof SessionFacts, value: string) => setTyped((t) => ({ ...t, [key]: value.trim() }));

  const loading = !!playerId && (player.loading || system.loading || custom.loading) && !(player.error || system.error);
  const error = player.error ?? system.error ?? custom.error ?? null;
  const ready = !!playerId && !!player.data && !loading && !error;
  // evaluated on every render: cheap, and it follows every staged edit of the tree at once
  const result = !ready
    ? null
    : evaluateCondition(rootId, {
      resolve: (id) => catalog.resolve(id),
      profiles: (name) => (name === SYSTEM_PROFILE || name === SYSTEM_COLLECTION ? systemValue : custom.data?.[short(name)]),
      now: gameNow,
      serverNow,
      session,
      product: (id) => catalog.products.get(id) as { itemId?: string | null } | undefined,
    });

  return { env, playerId, setPlayerId, at, setAt, serverNow, gameNow, session, setSessionFact, player, loading, error, result };
}

/** A collection's keys as one object, `{ generalInfo: {...}, smartInfo: {...}, … }` (players/PlayerTree.tsx's sections). */
function valueOf(detail: SaveCollectionDetail): Json {
  const out: Record<string, Json> = {};
  for (const k of detail.keys ?? []) out[k.key] = (k.value ?? null) as Json;
  return out;
}

// ---- the verdicts in the tree ------------------------------------------------------------------------------------------

/** The per-node verdicts the open builder shows while the preview runs; null when it does not. */
export const PreviewVerdicts = createContext<Map<string, LeafVerdict> | null>(null);

const TONES: Record<EvalState, Tone> = { pass: 'success', fail: 'danger', delayed: 'warning', unknown: 'neutral' };

/** The small badge of a leaf row (pass / fail / delayed / unknown, the reason as its title); nothing without a preview. */
export function PreviewVerdictBadge({ id }: { id: string }) {
  const verdicts = useContext(PreviewVerdicts);
  const v = verdicts?.get(id);
  if (!v) return null;
  return (
    <span className="preview-verdict" data-testid={`verdict-${id}`} data-state={v.state}>
      <Badge tone={TONES[v.state]} title={v.reason ? `${v.state}: ${v.reason}` : v.state}>
        {v.state}
      </Badge>
    </span>
  );
}

// ---- the panel ---------------------------------------------------------------------------------------------------------

/** The panel over the builder's tree (`children`), which receives the verdict badges. */
export function PlayerPreview({ rootId, children }: { rootId: string | null; children?: ReactNode }) {
  const preview = useConditionPreview(rootId);
  const [zone] = useTimeZone();
  const { offsets } = useGame();
  const { env, result } = preview;
  const when = formatInstantWithZone(preview.serverNow, zone, { offsets });
  return (
    <div className="player-preview" data-testid="player-preview">
      <section className="player-preview-panel" aria-label="Preview as player">
        <p className="muted small player-preview-note">A preview from the dashboard's reading of the engine rules (spec §5.1); the game decides.</p>
        <div className="player-preview-row">
          <EnvSelector label="Players of" />
        </div>
        {env ? <PlayerPicker env={env.slug} value={preview.playerId} onPick={preview.setPlayerId} /> : <p className="muted small">The game has no environment to read players from.</p>}
        <div className="player-preview-row">
          <Field label="Game time" className="player-preview-time">
            <DateTimeInput aria-label="Game time" value={preview.serverNow} onCommit={(v) => preview.setAt(v)} ctx={{ offsets }} />
          </Field>
          {preview.at !== null && (
            <Button size="s" variant="ghost" onClick={() => preview.setAt(null)}>
              Now
            </Button>
          )}
        </div>
        {preview.playerId && <SessionFields preview={preview} />}
        <div className="player-preview-verdict" role="status" aria-live="polite" data-testid="preview-verdict">
          {!preview.playerId ? (
            <span className="muted">Pick a player to see how this condition reads for them: their saved profiles are read from the chosen environment, at the game time above.</span>
          ) : preview.error ? (
            <span className="error">Cannot read the player: {errorText(preview.error)}</span>
          ) : !result ? (
            <span className="loading-line">
              <span className="spinner" aria-hidden="true" /> Reading the player…
            </span>
          ) : (
            <RootVerdict result={result} rootId={rootId} when={when} />
          )}
        </div>
      </section>
      <PreviewVerdicts.Provider value={result?.leaves ?? null}>{children}</PreviewVerdicts.Provider>
    </div>
  );
}

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : typeof e === 'object' && e && 'message' in e ? String((e as { message: unknown }).message) : String(e);
}

const GROUP_REASON = /^a condition inside/;

/** The first leaf reason of a state, for the root line (a group's own reason only says "inside"). */
function reasonOf(result: EvalResult, rootId: string | null, state: EvalState): string | undefined {
  for (const [, v] of result.leaves) if (v.state === state && v.reason && !GROUP_REASON.test(v.reason)) return v.reason;
  return rootId ? result.leaves.get(rootId)?.reason : undefined;
}

function RootVerdict({ result, rootId, when }: { result: EvalResult; rootId: string | null; when: string }) {
  const reason = reasonOf(result, rootId, result.state);
  switch (result.state) {
    case 'pass':
      return <Badge tone="success">Passes for this player at {when}</Badge>;
    case 'fail':
      return <Badge tone="danger">Fails for this player at {when}</Badge>;
    case 'delayed':
      return <Badge tone="warning">Delayed: {reason ?? 'waits for data the player has not saved'}</Badge>;
    default:
      return <Badge>Unknown: {reason ?? 'the preview cannot decide'}</Badge>;
  }
}

function PlayerPicker({ env, value, onPick }: { env: string; value: string; onPick: (id: string) => void }) {
  const { client } = useAuth();
  const { org, game, offsets } = useGame();
  const [zone] = useTimeZone();
  const recent = useQuery(`branch:${game.id}:env:${env}:players:recent:50`, () => client.data(ops.recentPlayers, { path: { org, game: game.slug, env }, query: { limit: 50 } }));
  const rows = ((recent.data as unknown as Page<Player> | undefined)?.items ?? []) as Player[];
  const [text, setText] = useState('');
  const [lookup, setLookup] = useState<{ state: 'idle' | 'loading' | 'none' | 'error'; message?: string }>({ state: 'idle' });
  useEffect(() => setLookup({ state: 'idle' }), [env]);
  const find = async () => {
    const v = text.trim();
    if (!v) return;
    setLookup({ state: 'loading' });
    try {
      for (const by of guessIdKinds(v)) {
        const page = (await client.data(ops.findPlayers, { path: { org, game: game.slug, env }, query: { by, value: v } })) as unknown as Page<Player>;
        const hit = (page.items ?? [])[0] as Player | undefined;
        if (hit) {
          setLookup({ state: 'idle' });
          onPick(hit.id);
          return;
        }
      }
      setLookup({ state: 'none' });
    } catch (err) {
      setLookup({ state: 'error', message: errorText(err) });
    }
  };
  const known = rows.some((p) => p.id === value);
  return (
    <div className="player-preview-row" role="search" aria-label="Find a player to preview">
      <Field label="Player">
        <Select aria-label="Recent player" value={known ? value : ''} onChange={(e) => onPick(e.target.value)}>
          <option value="">{value && !known ? shortPlayerId(value) : recent.loading ? 'Loading recent players…' : 'A recent player…'}</option>
          {rows.map((p) => (
            <option key={p.id} value={p.id}>
              {shortPlayerId(p.id)} · last seen {formatInstant(p.lastSeenAt, zone, { offsets })}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Or an id">
        <TextInput aria-label="Player id" placeholder="A user id, Nutaku id, device id, custom id or name" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void find();
            }
          }} />
      </Field>
      <Button size="s" onClick={() => void find()} disabled={!text.trim()} busy={lookup.state === 'loading'}>
        Look up
      </Button>
      {lookup.state === 'none' && <span className="muted small">No player found for “{text.trim()}”.</span>}
      {lookup.state === 'error' && <span className="error small">{lookup.message}</span>}
    </div>
  );
}

const SESSION_FIELDS: { key: keyof SessionFacts; label: string; placeholder: string }[] = [
  { key: 'appVersion', label: 'App version', placeholder: '1.0.0' },
  { key: 'engineVersion', label: 'Engine version', placeholder: '2022.3.1' },
  { key: 'platform', label: 'Platform id', placeholder: '11' },
  { key: 'balancyPlatform', label: 'Store platform id', placeholder: '3' },
  { key: 'language', label: 'Language', placeholder: 'en' },
  { key: 'country', label: 'Country', placeholder: 'DE' },
];

/** The session facts, prefilled from the player record and its GeneralInfo; a typed value wins, an empty one is "not known". */
function SessionFields({ preview }: { preview: ConditionPreview }) {
  return (
    <details className="player-preview-session">
      <summary className="small">Session facts (the system leaves read these)</summary>
      <div className="player-preview-row">
        {SESSION_FIELDS.map((f) => (
          <SessionInput key={f.key} label={f.label} placeholder={f.placeholder} value={String(preview.session[f.key] ?? '')} onCommit={(v) => preview.setSessionFact(f.key, v)} />
        ))}
      </div>
    </details>
  );
}

function SessionInput({ label, placeholder, value, onCommit }: { label: string; placeholder: string; value: string; onCommit: (v: string) => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <Field label={label} className="player-preview-fact">
      <TextInput size="s" aria-label={label} placeholder={placeholder} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => draft !== value && onCommit(draft)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onCommit(draft))} />
    </Field>
  );
}
